import React, { useState, useEffect, useRef } from 'react';
import { Truck, Search, Edit, Trash2, Plus, Phone, Mail, MapPin } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import ProveedorForm from './ProveedorForm';
import apiService from '../../services/apiService';
import './ProveedoresPage.css';

const ProveedoresPage = () => {
    const { loading, setLoading, setError, clearError } = useApp();
    const searchInputRef = useRef(null);

    const [proveedores, setProveedores] = useState([]);
    const [filteredProveedores, setFilteredProveedores] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedProveedor, setSelectedProveedor] = useState(null);
    const [filterActivo, setFilterActivo] = useState('todos');

    useEffect(() => {
        loadProveedores();
    }, []);

    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        filterProveedores();
    }, [proveedores, searchQuery, filterActivo]);

    const loadProveedores = async () => {
        try {
            setLoading(true);
            clearError();
            const result = await apiService.getProveedores();

            if (result.success) {
                setProveedores(result.data || []);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al cargar proveedores');
        } finally {
            setLoading(false);
        }
    };

    const filterProveedores = () => {
        let filtered = proveedores;

        // Filtro por búsqueda
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(proveedor =>
                proveedor.nombre?.toLowerCase().includes(query) ||
                proveedor.contacto?.toLowerCase().includes(query) ||
                proveedor.telefono?.toLowerCase().includes(query) ||
                proveedor.email?.toLowerCase().includes(query)
            );
        }

        // Filtro por estado activo
        if (filterActivo !== 'todos') {
            const esActivo = filterActivo === 'activos' ? 1 : 0;
            filtered = filtered.filter(proveedor => proveedor.activo === esActivo);
        }

        setFilteredProveedores(filtered);
    };

    const handleCreateProveedor = async (proveedorData) => {
        try {
            setLoading(true);
            const result = await apiService.crearProveedor(proveedorData);

            if (result.success) {
                setShowCreateModal(false);
                loadProveedores();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al crear proveedor');
        } finally {
            setLoading(false);
        }
    };

    const handleEditProveedor = async (proveedorData) => {
        try {
            setLoading(true);
            const result = await apiService.actualizarProveedor({
                id: selectedProveedor.id,
                ...proveedorData
            });

            if (result.success) {
                setShowEditModal(false);
                setSelectedProveedor(null);
                loadProveedores();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al actualizar proveedor');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteProveedor = async (id, nombre) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar el proveedor "${nombre}"?`)) {
            return;
        }

        try {
            setLoading(true);
            const result = await apiService.eliminarProveedor(id);

            if (result.success) {
                loadProveedores();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al eliminar proveedor');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="proveedores-page">
            {/* Controles */}
            <div className="proveedores-controls">
                <div className="search-filters">
                    <div className="search-container">
                        <Input
                            ref={searchInputRef}
                            placeholder="Buscar por nombre, contacto, teléfono o email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            icon={<Search size={20} />}
                            className="search-input"
                        />
                    </div>
                    <div className="filter-container">
                        <select
                            value={filterActivo}
                            onChange={(e) => setFilterActivo(e.target.value)}
                            className="status-filter"
                        >
                            <option value="todos">Todos</option>
                            <option value="activos">Activos</option>
                            <option value="inactivos">Inactivos</option>
                        </select>
                    </div>
                </div>
                <div className="controls-actions">
                    <Button
                        onClick={() => setShowCreateModal(true)}
                        disabled={loading}
                    >
                        <Plus size={18} />
                        Nuevo Proveedor
                    </Button>
                </div>
            </div>

            {/* Tabla de proveedores */}
            <div className="proveedores-table-container">
                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Cargando proveedores...</p>
                    </div>
                )}

                {!loading && filteredProveedores.length === 0 && (
                    <div className="empty-state">
                        <Truck size={48} />
                        <h3>No se encontraron proveedores</h3>
                        <p>
                            {searchQuery
                                ? 'No hay proveedores que coincidan con la búsqueda'
                                : 'No hay proveedores registrados'
                            }
                        </p>
                        {!searchQuery && (
                            <Button onClick={() => setShowCreateModal(true)}>
                                Crear primer proveedor
                            </Button>
                        )}
                    </div>
                )}

                {!loading && filteredProveedores.length > 0 && (
                    <div className="proveedores-table-wrapper">
                        <table className="proveedores-table">
                            <thead>
                                <tr>
                                    <th>Nombre</th>
                                    <th>Contacto</th>
                                    <th>Teléfono</th>
                                    <th>Email</th>
                                    <th>RFC</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProveedores.map((proveedor) => (
                                    <tr key={proveedor.id}>
                                        <td>
                                            <div className="proveedor-nombre">

                                                {proveedor.nombre}
                                            </div>
                                        </td>
                                        <td>{proveedor.contacto || '-'}</td>
                                        <td>
                                            {proveedor.telefono ? (
                                                <div className="contact-info">

                                                    {proveedor.telefono}
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td>
                                            {proveedor.email ? (
                                                <div className="contact-info">
                                                    <Mail size={14} />
                                                    {proveedor.email}
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td>{proveedor.rfc || '-'}</td>
                                        <td>
                                            <span className={`status-badge ${proveedor.activo ? 'active' : 'inactive'}`}>
                                                {proveedor.activo ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="actions-compact">
                                                <button
                                                    className="action-btn-compact"
                                                    onClick={() => {
                                                        setSelectedProveedor(proveedor);
                                                        setShowEditModal(true);
                                                    }}
                                                    title="Editar"
                                                >
                                                    <Edit size={14} />
                                                </button>
                                                <button
                                                    className="action-btn-compact delete"
                                                    onClick={() => handleDeleteProveedor(proveedor.id, proveedor.nombre)}
                                                    title="Eliminar"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal Crear */}
            {showCreateModal && (
                <Modal
                    title="Nuevo Proveedor"
                    onClose={() => setShowCreateModal(false)}
                    size="medium"
                >
                    <ProveedorForm
                        onSubmit={handleCreateProveedor}
                        onCancel={() => setShowCreateModal(false)}
                        isLoading={loading}
                    />
                </Modal>
            )}

            {/* Modal Editar */}
            {showEditModal && selectedProveedor && (
                <Modal
                    title="Editar Proveedor"
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedProveedor(null);
                    }}
                    size="medium"
                >
                    <ProveedorForm
                        proveedor={selectedProveedor}
                        onSubmit={handleEditProveedor}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedProveedor(null);
                        }}
                        isLoading={loading}
                        isEdit={true}
                    />
                </Modal>
            )}
        </div>
    );
};

export default ProveedoresPage;
